import { getFleets } from "./generated/calls/fleets/fleets";
import { getRpcCurrentFleetId } from "./generated/calls/rpc-current-fleet-id/rpc-current-fleet-id";
import type { Fleets as FleetDTO } from "./generated/models";
import { buildHeaders } from "./helpers/headerHelper";
import { postgrestError } from "./helpers/postgrestErrorHelper";
import { postgrestFilter } from "./helpers/queryHelper";
import type { APIResult } from "./types";

export type { FleetDTO };

/**
 * Fetches the current user's fleet data from the API.
 *
 * `GET /fleets` isn't scoped by row-level security to the caller's own
 * fleet - it returns every fleet in the database - so the current fleet id
 * is resolved via `current_fleet_id()` first (mirrors
 * `resolveAndFetchUsers`'s use of `current_user_id()`) and `/fleets` is then
 * queried by that id specifically.
 *
 * Returns an API result containing the fetched fleet, or a controlled
 * failure if the request could not be completed.
 */
export async function fetchFleet(): Promise<APIResult<FleetDTO>> {
  try {
    const headers = buildHeaders();

    const currentFleetIdRes = await getRpcCurrentFleetId({ headers });

    if (currentFleetIdRes.status !== 200) {
      throw postgrestError(
        "Resolving the current fleet failed",
        currentFleetIdRes,
      );
    }

    const fleetId = currentFleetIdRes.data;

    if (!fleetId) {
      return {
        success: false,
        errorMsg: "The current user has no associated fleet",
      };
    }

    const fleetFetchRes = await getFleets(
      { id: postgrestFilter(fleetId) },
      { headers },
    );

    switch (fleetFetchRes.status) {
      // A read narrowed by `limit`, `offset` or a `Range` header answers 206
      // and carries the same rows as a 200.
      case 200:
      case 206:
        if (fleetFetchRes.data.length !== 1) {
          throw new Error(
            `Expected exactly one fleet for id ${fleetId}, received ${fleetFetchRes.data.length}`,
          );
        }
        return {
          success: true,
          data: fleetFetchRes.data[0]!,
        };
      default:
        throw postgrestError("Fetch failed", fleetFetchRes);
    }
  } catch (error) {
    return {
      success: false,
      errorMsg:
        error instanceof Error ? error.message : "an unknown error occurred",
    };
  }
}
